import { useCallback, useRef } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useMediaQuery } from '@/extensions/snk/hooks/use-media-query';
import { useNavigate } from '@/hooks/use-navigate';
import {
    FILTERS_CLOSED,
    FILTERS_OPEN,
    FILTERS_QUERY_PARAM,
    getInitialFiltersOpen,
} from '@/hooks/use-filters-panel-state';

/** The drawer only exists below `lg`; at and above it the sidebar is always on screen. */
const DESKTOP_QUERY = '(min-width: 1024px)';

export interface FilterOverlayState {
    /** Whether the drawer is open. Derived from `?filters`, never stored. */
    open: boolean;
    /** Whether the lazy subtree may render. Latches on first open and never goes back. */
    mounted: boolean;
    setOpen: (open: boolean) => void;
}

/**
 * `?filters=open` owns the drawer, so a shared link, a refresh and back/forward all agree with what is on
 * screen. The param is stripped by `shouldRevalidate` and by the grid/analytics identity keys, so toggling
 * it re-runs no loader and re-keys nothing.
 *
 * `mounted` is deliberately not the same signal: it latches, both to keep the lazy chunk off the first
 * paint and because unmounting `Drawer.Root` the instant `open` flips would skip Radix's close sequence
 * (and with it `onCloseAutoFocus`, which is what returns focus to the Filter button).
 */
export function useFilterOverlayState(): FilterOverlayState {
    const location = useLocation();
    const navigation = useNavigation();
    const navigate = useNavigate();

    // Read the in-flight target so applying a refine from inside the drawer doesn't flicker it shut.
    const pendingSearch = navigation.location?.pathname === location.pathname ? navigation.location.search : undefined;
    const isDesktop = useMediaQuery(DESKTOP_QUERY);
    const open = !isDesktop && getInitialFiltersOpen(new URLSearchParams(pendingSearch ?? location.search));

    const mountedRef = useRef(open);
    if (open) mountedRef.current = true;

    const setOpen = useCallback(
        (next: boolean) => {
            const params = new URLSearchParams(location.search);
            params.set(FILTERS_QUERY_PARAM, next ? FILTERS_OPEN : FILTERS_CLOSED);
            // No pathname: `useNavigate` would re-apply the site prefix to an already-prefixed path.
            void navigate({ search: `?${params.toString()}` }, { replace: true, preventScrollReset: true });
        },
        [location.search, navigate]
    );

    return { open, mounted: mountedRef.current, setOpen };
}
